import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Coffee } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BreakPolicyCard = ({ token }) => {
  const { toast } = useToast();
  const [policy, setPolicy] = useState('unpaid');
  const [threshold, setThreshold] = useState('5');
  const [reminders, setReminders] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/company/break-policy`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setPolicy(r.data.break_policy_default || 'unpaid');
        setThreshold(String(r.data.break_threshold_hours ?? 5));
        setReminders(r.data.break_reminder_enabled !== false);
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company/break-policy`, {
        break_policy_default: policy,
        break_threshold_hours: parseFloat(threshold),
        break_reminder_enabled: reminders,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Break policy saved', description: 'Applies to clock-outs from now on. Staff with a per-person override keep their own setting.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-lg" data-testid="break-policy-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Coffee className="w-4 h-4 text-amber-400" /> Meal Breaks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Company default</Label>
          <Select value={policy} onValueChange={setPolicy}>
            <SelectTrigger data-testid="break-policy-default-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unpaid" data-testid="break-policy-option-unpaid">Unpaid — 30 min deducted at clock-out when a break is confirmed</SelectItem>
              <SelectItem value="paid" data-testid="break-policy-option-paid">Paid — breaks stay on the clock, nothing deducted</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">Each staff member can be overridden in Manage Users → edit staff.</p>
        </div>
        <div>
          <Label>Break required after (hours on shift)</Label>
          <Select value={threshold} onValueChange={setThreshold}>
            <SelectTrigger data-testid="break-threshold-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['3', '4', '4.5', '5', '5.5', '6', '7', '8', '9', '10'].map((h) => (
                <SelectItem key={h} value={h} data-testid={`break-threshold-option-${h}`}>{h} hours</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">Sets when the clock-out break check and reminders kick in (unpaid-break staff only see the clock-out prompt).</p>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label>Break reminder notifications</Label>
            <p className="text-xs text-gray-500">Nudge workers in-app shortly before the threshold so the break gets taken and logged.</p>
          </div>
          <Switch checked={reminders} onCheckedChange={setReminders} data-testid="break-reminder-switch" />
        </div>
        <Button onClick={save} disabled={saving} className="w-full" data-testid="break-policy-save-btn">
          {saving ? 'Saving…' : 'Save break policy'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default BreakPolicyCard;
